import { useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { DeleteIcon, EditIcon, LockIcon, QrCodeIcon } from '../theme/Icons';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { Text } from '../components/Text';
import { IAction, Menu } from '../components/Menu';
import { MemberAvatar } from './MemberAvatar';
import { CareCodeModalState } from '../share-caremap/care-code/CareCodeModalState';
import { formatAge } from '../utils/format';
import { ContactMethodsList } from '../contact-methods/ContactMethodsList';
import { NameHelper } from '../contact-methods/NameHelper';
import { CareMapNotesList } from '../notes/note-list/CareMapNotesList';
import { DiagnosisList } from './DiagnosisList';
import { AppointmentsList } from '../appointments/AppointmentsList';
import { DrawerListSection } from '../components/DrawerListSection';
import { AppointmentsState, NEW_APPOINTMENT_ID } from '../appointments/AppointmentsState';
import { AllergyList } from './AllergyList';
import { EditPatientNudgeBanner } from './patient/EditPatientNudgeBanner';
import { CaremapNotesModelFactory } from '../notes//model/CaremapNotesModelFactory';
import { AlertPresenter } from '../AlertBinder';
import { MedicationList } from './patient/MedicationList';
import { CaremapMemoriesModelFactory } from '../memories/CaremapMemoriesModelFactory';
import { MemoryList } from '../memories/MemoryList';
import { SharingAndAccessState } from '../share-caremap/sharing-and-access/SharingAndAccessState';
import { SharingAndAccessSheet as PrototypeSharingAndAccessSheet } from '../_prototype/sharing-and-access/SharingAndAccessSheet';
import { useDevSettings } from '../utils/useDevSettings';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { CareMapDrawerTrackingHelper } from '../caremap/CareMapDrawerTrackingHelper';

interface IPatientViewProps {
    hideNotes?: boolean;
    hideAppointments?: boolean;
    onEdit: () => void;
    onMedicationClick: (id: string) => void;
    onAddMedication: () => void;
    onAllergyClick: (id: string) => void;
    onAddAllergy: () => void;
    onDiagnosisClick: (id: string) => void;
    onAddDiagnosis: () => void;
    excludeDOBInNudge?: boolean;
    allowDelete?: boolean;
}

/**
 * This is being used by both CarePage and PatientDrawerView, b/c at this time they have more in common than they are different
 *
 * We should abandon it at the point that the 2 concerns diverge too much.
 */
export const ActivePatientView: React.FC<IPatientViewProps> = ({
    hideAppointments,
    hideNotes,
    onEdit,
    onMedicationClick,
    onAddMedication,
    onAllergyClick,
    onAddAllergy,
    onDiagnosisClick,
    onAddDiagnosis,
    excludeDOBInNudge,
    allowDelete
}) => {
    const nameHelper = useInstance(NameHelper);
    const alert = useInstance(AlertPresenter);
    const careMapDrawerTrackingHelp = useInstance(CareMapDrawerTrackingHelper);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const memoriesFactory = useInstance(CaremapMemoriesModelFactory);

    const appointmentsState = useInstance(AppointmentsState);
    useSubscription(() => appointmentsState);

    const [isSharingSettingsOpen, setIsSharingSettingsOpen] = useState(false);
    const sharingState = useInstance(SharingAndAccessState);
    const careCodeModal = useInstance(CareCodeModalState);

    // Dev-only: compare the real Sharing & Access sheet against the throwaway prototype.
    const devSettings = useDevSettings();

    const canEdit = careMapState.hasPermissionTo('put');

    const caremap = careMapState.getActiveCaremap();

    const history = useInstance(HistoryViewModel);

    const isViewer = !careMapState.hasPermissionTo('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const canAddEdit = !isViewer && !isAnonymous;

    const patient = caremap?.patient;

    if (!caremap || !patient) {
        return null;
    }

    const viewMoreNotes = () => {
        history.push(`/caremaps/${careMapState.getActiveCaremap()?.careMapId}/drawer/household/member/patient/notes`);
    };

    const editNote = (noteId: string) => {
        history.push(`/caremaps/${careMapState.getActiveCaremap()?.careMapId}/drawer/household/member/patient/notes/${noteId}`);
    };

    const addNote = () => {
        history.push(`/caremaps/${careMapState.getActiveCaremap()?.careMapId}/drawer/household/member/patient/notes/add-note`);
    };

    const viewMoreAppointments = () => {
        history.push(`/caremaps/${appointmentsState.getCareMapId()}/drawer/household/member/patient/appointments`);
    };

    const editAppointment = (appointmentId?: string) => {
        if (appointmentId === NEW_APPOINTMENT_ID) {
            appointmentsState.trackNewAppointmentOpened({});
        }

        history.push(`/caremaps/${appointmentsState.getCareMapId()}/drawer/household/member/patient/appointments/${appointmentId}`);
    };

    const deleteCareRecipient = async () => {
        const destroyOrCancel = await alert.confirmDestructiveAction({
            wider: true,
            header: `Delete ${careMapState.getPatientFirstName()} as a Care Recipient?`,
            message: 'All info including contacts, notes, photos and everything associated with this care recipient will be deleted and cannot be recovered.',
        });

        if (destroyOrCancel === 'destroy'){
            try {
                await careMapState.delete();
            }
            catch (err){
                alert.showAndLogError(err);
            }
        }
    };

    const listShowCount = 2;
    const notes = notesFactory.forActiveCaremap().getNotesForTarget({ section: 'patient' });

    const editMenuOptions: IAction[] = [
        { Icon: EditIcon, text: `Edit Care Recipient`, handler: onEdit },
        {
            Icon: LockIcon,
            text: `Sharing & Access`,
            handler: () => devSettings.showSharingPrototype ? setIsSharingSettingsOpen(true) : sharingState.open('Care Recipient Menu'),
            'data-type': 'sharing-settings'
        }
    ];

    // A Care Code needs a care recipient to point at, so it stays hidden until the care profile
    // has been set up. The server only grants createViewerAccessToken to people allowed to share,
    // which also keeps it away from viewers.
    if (caremap && !careMapState.isIncomplete(caremap) && caremap.links.createViewerAccessToken) {
        editMenuOptions.push({
            Icon: QrCodeIcon,
            text: `Care Code`,
            handler: () => careCodeModal.open('Care Recipient Menu'),
            'data-type': 'care-code'
        });
    }

    if (allowDelete && careMapState.hasPermissionTo('delete')) {
        editMenuOptions.push({
            role: 'destructive',
            Icon: DeleteIcon,
            text: `Delete Care Recipient`,
            handler: deleteCareRecipient,
            'data-type': 'delete-caremap-item'
        });
    }

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15, paddingBottom: 25 }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: 20 }}>

                    <MemberAvatar member={patient} />

                    <div style={{ display: 'flex', flexGrow: 1, flexDirection: 'column', gap: 4 }}>
                        <Text type="title" data-type="title"><span className="mp-mask">{patient.firstName || ''} {patient.lastName || ''}{careMapState.isUserThePatient() ? <span style={{ fontWeight: 'normal' }}> (you)</span> : ''}</span></Text>
                        <Text type="subtitle" data-type="subtitle">{formatAge(patient.dob)}</Text>
                    </div>

                    {canEdit &&
                        <div style={{marginTop: -8}}>
                            <Menu actions={editMenuOptions} />
                        </div>
                    }
                </div>

                <EditPatientNudgeBanner
                    onEdit={onEdit}
                    excludeDOB={excludeDOBInNudge}
                />

                <ContactMethodsList
                    contactMethods={patient.contactMethods}
                    address={patient.address}
                    iconWidth={64}
                    emailToName={nameHelper.getName(patient)}
                    trackingContext={{ category: 'patient' }}
                />

                {/* we hide this in the new care ui b/c this assumes its in a caremap drawer, and so we would have to invest
                    in bridging the gap to get it to work
                    and it will probably be its own top level nav in the long run, and so this would be waste to get it working here
                 */}
                {!hideNotes && (
                    <DrawerListSection>
                        <CareMapNotesList
                            data-type="patient-notes-list"
                            showCount={listShowCount}
                            onNoteClicked={editNote}
                            onAddNote={addNote}
                            moreNotesHandler={viewMoreNotes}
                            notes={notes}
                        />
                    </DrawerListSection>
                )}

                {/* we hide this in the new care ui b/c this assumes its in a caremap drawer, and so we would have to invest
                    in bridging the gap to get it to work
                    and it will be its own top level nav in the long run, and so this would be waste to get it working here
                 */}
                {!hideAppointments && (
                    <DrawerListSection>
                        <AppointmentsList
                            showViewMore
                            numDaysToShow={1}
                            ignoreHasScrolled
                            showCount={listShowCount}
                            minDate={(new Date()).toISOString()}
                            editAppointment={editAppointment}
                            viewMore={viewMoreAppointments}
                            onViewRecording={(noteId) => {
                                careMapDrawerTrackingHelp.setTrackingFrom('Appointment');
                                history.push(`/caremaps/${careMapState.getActiveCaremap()?.careMapId}/drawer/household/member/patient/notes/${noteId}`);
                            }}
                            data-type="appointments-list"
                        />
                    </DrawerListSection>
                )}

                <DiagnosisList
                    patient={patient}
                    canAddEdit={canAddEdit}
                    diagnoses={patient.diagnoses}
                    onAdd={onAddDiagnosis}
                    onClick={(id) => onDiagnosisClick(id)}
                />

                <AllergyList
                    patient={patient}
                    canAddEdit={canAddEdit}
                    allergies={patient.allergies}
                    onAdd={onAddAllergy}
                    onClick={(id) => onAllergyClick(id)}
                />

                <MedicationList
                    patient={patient}
                    canAddEdit={canAddEdit}
                    medications={patient.medications}
                    onAdd={onAddMedication}
                    onClick={(id) => onMedicationClick(id)}
                />

                <div style={{ marginTop: 25 }}>
                    <MemoryList model={memoriesFactory.forActiveCaremap()} />
                </div>
            </div>

            {/* Real sheet is mounted globally (GlobalSharingAndAccessSheet); this dev-only
                branch renders the throwaway prototype for side-by-side comparison. */}
            {devSettings.showSharingPrototype && (
                <PrototypeSharingAndAccessSheet
                    isOpen={isSharingSettingsOpen}
                    onDidDismiss={() => setIsSharingSettingsOpen(false)}
                />
            )}
        </>
    );
};
